import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, CheckCircle2, FileText, BarChart3 } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

export default function EnergitjekPage() {
  return (
    <main data-testid="energitjek-page" className="pt-24">
      <Helmet>
        <title>Energitjek | Gratis energigennemgang & dokumenteret besparelse | Dilling El</title>
        <meta name="description" content="Få tal på jeres besparelse med vores gratis energigennemgang eller det fulde Energitjek Plus med måling, dokumentation og rapport. Dilling El, Roskilde." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://dillingel.dk/ydelser/energitjek" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dillingel.dk/ydelser/energitjek" />
        <meta property="og:title" content="Energitjek | Gratis energigennemgang & dokumenteret besparelse | Dilling El" />
        <meta property="og:description" content="Få tal på jeres besparelse med vores gratis energigennemgang eller det fulde Energitjek Plus med måling, dokumentation og rapport." />
        <meta property="og:image" content="https://dillingel.dk/images/og-image.jpg" />
        <meta property="og:locale" content="da_DK" />
        <meta property="og:site_name" content="Dilling El ApS" />
      </Helmet>

      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#F4F2EE]">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="w-16 h-16 flex items-center justify-center mb-8">
                <Zap size={48} strokeWidth={1} className="text-[#0E3D24]" />
              </div>
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#A6B8A2]">
                Ydelser
              </span>
              <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl lg:text-6xl font-medium text-[#0E3D24] mt-4 leading-[1.1]">
                Energitjek – få tal på jeres besparelse
              </h1>
              <p className="text-[#1E2A24]/70 text-lg mt-6 leading-relaxed">
                Vi tager hele forløbet, fra gennemgang til udførelse og eftermåling, så I kan se resultatet sort på hvidt. Når Energistyrelsens erhvervspulje er åben, søger vi også tilskud for jer.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Gratis energigennemgang */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 bg-[#0E3D24] text-white px-4 py-2 text-sm mb-6">
                <span>Gratis</span>
              </div>
              <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-medium text-[#0E3D24] leading-tight">
                Gratis energigennemgang
              </h2>
              <p className="text-[#1E2A24]/70 text-lg mt-6 leading-relaxed">
                Vi kommer ud og gennemgår belysning, styring og installationer. I får et overslag over, hvad I kan spare, og vores bud på de vigtigste tiltag.
              </p>
              <p className="text-[#0E3D24] font-medium mt-4">
                Gratis og uforpligtende.
              </p>
              <Link 
                to="/kontakt"
                className="inline-flex items-center gap-3 mt-10 bg-[#0E3D24] text-white px-8 py-4 text-sm tracking-[0.05em] hover:bg-[#1F5A3A] transition-colors group"
                data-testid="energitjek-cta-free"
              >
                <span>Book gratis gennemgang</span>
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-[#F4F2EE] p-8 md:p-12"
            >
              <h3 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#0E3D24] mb-6">
                Hvad I får
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle2 size={20} className="text-[#0E3D24] mt-0.5 flex-shrink-0" />
                  <span className="text-[#1E2A24]/70">Gennemgang af belysning og installationer</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 size={20} className="text-[#0E3D24] mt-0.5 flex-shrink-0" />
                  <span className="text-[#1E2A24]/70">Overslag over besparelsespotentiale</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 size={20} className="text-[#0E3D24] mt-0.5 flex-shrink-0" />
                  <span className="text-[#1E2A24]/70">Prioriteret liste over de vigtigste tiltag</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 size={20} className="text-[#0E3D24] mt-0.5 flex-shrink-0" />
                  <span className="text-[#1E2A24]/70">Uforpligtende og uden omkostninger</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Energitjek Plus */}
      <section className="py-24 md:py-32 bg-[#0E3D24]">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 bg-white text-[#0E3D24] px-4 py-2 text-sm mb-6">
                <span>8.995 kr. ekskl. moms</span>
              </div>
              <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-medium text-white leading-tight">
                Energitjek Plus
              </h2>
              <p className="text-white/70 text-lg mt-6 leading-relaxed">
                Til virksomheder, der skal bruge dokumentation over for bank, kunder eller myndigheder.
              </p>
              <p className="text-[#A6B8A2] mt-6 text-sm">
                Prisen modregnes, hvis I får os til at udføre tiltagene.
              </p>
              <Link 
                to="/kontakt"
                className="inline-flex items-center gap-3 mt-10 bg-white text-[#0E3D24] px-8 py-4 text-sm tracking-[0.05em] hover:bg-[#F4F2EE] transition-colors group"
                data-testid="energitjek-cta-plus"
              >
                <span>Bestil Energitjek Plus</span>
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <h3 className="font-['Cormorant_Garamond'] text-2xl font-medium text-white mb-6">
                Hvad I får
              </h3>
              <ul className="space-y-5">
                <li className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/10 flex-shrink-0">
                    <BarChart3 size={20} className="text-white" />
                  </div>
                  <div>
                    <span className="text-white font-medium">14 dages måling</span>
                    <p className="text-white/60 text-sm mt-1">På hovedtavle og de største forbrugere</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/10 flex-shrink-0">
                    <Zap size={20} className="text-white" />
                  </div>
                  <div>
                    <span className="text-white font-medium">Lysmåling og driftstider</span>
                    <p className="text-white/60 text-sm mt-1">Lux-måling og gennemgang af faktisk brug</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/10 flex-shrink-0">
                    <FileText size={20} className="text-white" />
                  </div>
                  <div>
                    <span className="text-white font-medium">CO₂-opgørelse (Scope 2)</span>
                    <p className="text-white/60 text-sm mt-1">Dokumentation til jeres ESG-arbejde</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/10 flex-shrink-0">
                    <CheckCircle2 size={20} className="text-white" />
                  </div>
                  <div>
                    <span className="text-white font-medium">Beregning af besparelse</span>
                    <p className="text-white/60 text-sm mt-1">Tilbagebetalingstid og mulige tilskud</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/10 flex-shrink-0">
                    <FileText size={20} className="text-white" />
                  </div>
                  <div>
                    <span className="text-white font-medium">Rapport på én side</span>
                    <p className="text-white/60 text-sm mt-1">Med konkrete forslag I kan handle på</p>
                  </div>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#F4F2EE]">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-medium text-[#0E3D24] mb-6">
              Hele forløbet, fra start til slut
            </h2>
            <p className="text-[#1E2A24]/70 text-lg max-w-2xl mx-auto mb-10">
              Vi tager hele forløbet, fra gennemgang til udførelse og eftermåling, så I kan se resultatet sort på hvidt. Når Energistyrelsens erhvervspulje er åben, søger vi også tilskud for jer.
            </p>
            <Link 
              to="/kontakt"
              className="inline-flex items-center gap-3 bg-[#0E3D24] text-white px-8 py-4 text-sm tracking-[0.05em] hover:bg-[#1F5A3A] transition-colors group"
            >
              <span>Kom i gang – book en gratis gennemgang</span>
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
